import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import {
    Paper, Grid, Typography, ButtonGroup,
    Button, Divider, IconButton, LinearProgress, Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { setViewType } from '../../features/tripcenter/tripViewSlice';
import { setRouteMaker, setCreatedMessage, setErrorMessage } from '../../features/tripcenter/routeSlice';
import { resetRouteDetails } from '../../features/tripcenter/googleRouteMakerSlice';
import { mapServiceSelector, setMapControl } from '../../features/map/mapSlice';
import { resetAllTrips } from '../../features/tripcenter/tripsApi';
import { userSelector } from '../../features/user/UserSlice';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TripSearchPanel from './tripComponents/TripSearchPanel';
import { tripStageSearch, getActiveTrips } from '../../features/tripcenter/tripsApi';
import { searchLoadingSelector, setSearchStage, searchStageSelector, getTripSummary } from '../../features/tripcenter/tripControlSlice';
import TripSummaryModal from './tripComponents/TripSummaryModal';
import TripPerformanceModal from './tripComponents/TripPerformanceModal';
import DeleteIcon from '@mui/icons-material/Delete';
import TripResetDialog from './TripResetDialog';

const TripsSideBarTop = () => {
    const dispatch = useDispatch();
    const viewType = useSelector((state) => state.tripView.viewType);
    const mapService = useSelector(mapServiceSelector);
    const searchLoading = useSelector(searchLoadingSelector);
    const searchStage = useSelector(searchStageSelector);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const { isAdmin } = useSelector(userSelector);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);

    const setTripView = () => {
        dispatch(setViewType('tripList'));
        dispatch(setRouteMaker(null));
    };

    const setRouteView = () => {
        dispatch(setViewType('routes'));
        dispatch(setRouteMaker(null));
    };

    const launchRouteBuilderWizard = () => {
        dispatch(setMapControl('googleRoute'));
        dispatch(setRouteMaker('google'));
        dispatch(resetRouteDetails());
        dispatch(setCreatedMessage(null));
        dispatch(setErrorMessage(null));
    };

    const tripSearch = (stage) => {
        dispatch(setSearchStage(stage));
        if (stage === 'Active') {
            dispatch(getActiveTrips());
        } else {
            dispatch(tripStageSearch({ tripStage: stage }));
        }
    };

    const launchTripSearch = () => {
        dispatch(setSearchStage('Search'));
        setShowSearchModal(true);
    };

    useEffect(() => {
        console.log("searchStage", searchStage);
    }, [searchStage]);

    const handleResetSubmit = (payload) => {
        dispatch(resetAllTrips(payload));
        setResetDialogOpen(false);
    };

    const tripListView = () => {
        if (searchLoading) {
            return (
                <Box sx={{ width: '100%' }}>
                    <LinearProgress />
                </Box>
            );
        } else {
            return (
                <ButtonGroup variant="text" size="small" color="primary">
                    <Button
                        onClick={() => tripSearch('Planned')}
                        variant={searchStage === 'Planned' ? 'contained' : 'text'}
                    >Planned</Button>
                    <Button
                        onClick={() => tripSearch('Active')}
                        variant={searchStage === 'Active' ? 'contained' : 'text'}
                    >Active</Button>
                    <Button
                        onClick={() => tripSearch('Complete')}
                        variant={searchStage === 'Complete' ? 'contained' : 'text'}
                    >Complete</Button>
                    {/* <Button
                        onClick={() => tripSearch('All')}
                        variant={searchStage === 'All' ? 'contained' : 'text'}
                    >All</Button> */}
                    <IconButton
                        onClick={launchTripSearch}
                        sx={{
                            bgcolor: searchStage === 'Search' ? 'primary.main' : 'transparent',
                            color: searchStage === 'Search' ? 'white' : 'primary.main',
                            padding: 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                bgcolor: searchStage === 'Search' ? 'primary.dark' : 'action.hover'
                            }
                        }}
                    >
                        <SearchIcon fontSize="small" />
                    </IconButton>
                    {isAdmin && (
                        <IconButton
                            onClick={() => setResetDialogOpen(true)}
                        >
                            <DeleteIcon fontSize="small" sx={{ color: 'red' }} />
                        </IconButton>
                    )}
                    <TripSummaryModal />
                    <TripPerformanceModal />
                </ButtonGroup>
            );
        }
    }

    const routeListView = () => {
        return (
            <Grid container spacing={1} style={{ width: '95%' }}>
                <Grid item sm={12}>
                    <ButtonGroup variant="text" size="small" color="primary">
                        <Button
                            onClick={launchRouteBuilderWizard}
                            disabled={mapService !== 'google'}
                            endIcon={<AddCircleOutlineIcon />}
                        >
                            New Route
                        </Button>
                    </ButtonGroup>
                </Grid>
            </Grid>
        );
    }

    return (
        <div>
            <Paper>
                <div style={{ marginLeft: '15px', marginRight: '5px', marginBottom: '5px' }}>
                    <Grid container spacing={1}>
                        <Grid item sm={12}>
                            <Grid container spacing={1} justifyContent="space-between">
                                <Grid item>
                                    <Typography variant="h6" style={{ display: 'inline' }}>
                                        <b>{viewType === 'tripList' ? 'Trip Dashboard' : 'My Routes'}</b>
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <ButtonGroup variant="text" size="small" color="primary">
                                        <Button onClick={() => setTripView()} variant={viewType === 'tripList' ? 'contained' : 'text'}>Trips</Button>
                                        <Button onClick={() => setRouteView()} variant={viewType === 'routes' ? 'contained' : 'text'}>Routes</Button>
                                    </ButtonGroup>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item sm={12}><Divider /></Grid>
                        {viewType === 'tripList' && <Grid item sm={12}>{tripListView()}</Grid>}
                        {viewType === 'routes' && <Grid item sm={12}>{routeListView()}</Grid>}
                        <Grid item sm={12}></Grid>
                    </Grid>
                </div>
                <TripSearchPanel
                    show={showSearchModal}
                    onHide={() => setShowSearchModal(false)}
                />
            </Paper>
            <TripResetDialog
                open={resetDialogOpen}
                onClose={() => setResetDialogOpen(false)}
                onSubmit={handleResetSubmit}
            />
        </div>
    );
}

export default TripsSideBarTop;
